import { useState } from 'react';
import type { TestPersona } from '@verifyloop/core';
import type { ProjectSummary } from '../api/types';
import { patchProject } from '../api/endpoints';
import { describeError } from '../api/client';
import { useToast } from '../state';
import { SectionTitle } from './Feedback';
import { ConfirmButton, TextField } from './Form';
import { Mono } from './Pills';

const DEFAULT_PERSONAS_TEMPLATE: TestPersona[] = [
  {
    id: 'standard',
    label: 'Standard User',
    email: 'test-user@prakhya.id',
    password: 'TestPassword123!',
    role: 'user',
  },
  {
    id: 'admin',
    label: 'Admin User',
    email: 'admin-user@prakhya.id',
    password: 'AdminPassword123!',
    role: 'admin',
  },
];

export function PersonasTab({
  project,
  onProjectUpdated,
}: {
  project: ProjectSummary;
  onProjectUpdated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [personas, setPersonas] = useState<TestPersona[]>(() => {
    return project.personas && project.personas.length > 0
      ? project.personas
      : DEFAULT_PERSONAS_TEMPLATE;
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  // Form states
  const [formId, setFormId] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('user');
  const [saving, setSaving] = useState(false);

  const startEdit = (p: TestPersona) => {
    setAddingNew(false);
    setEditingId(p.id);
    setFormId(p.id);
    setFormLabel(p.label);
    setFormEmail(p.email);
    setFormPassword(p.password ?? '');
    setFormRole(p.role ?? 'user');
  };

  const startNew = () => {
    setEditingId(null);
    setAddingNew(true);
    setFormId('');
    setFormLabel('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('user');
  };

  const cancelForm = () => {
    setEditingId(null);
    setAddingNew(false);
  };

  const savePersonasToBackend = async (nextList: TestPersona[]): Promise<void> => {
    setSaving(true);
    try {
      await patchProject(project.id, { personas: nextList });
      setPersonas(nextList);
      toast.success('Test Personas updated', 'Credentials will be injected into {{user.*}} tokens');
      onProjectUpdated();
      cancelForm();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForm = async () => {
    const trimmedId = formId.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '');
    if (!trimmedId) {
      toast.error('Persona ID is required (e.g. standard, admin)');
      return;
    }
    if (!formEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    const item: TestPersona = {
      id: trimmedId,
      label: formLabel.trim() || trimmedId,
      email: formEmail.trim(),
      password: formPassword.trim() || undefined,
      role: formRole.trim() || 'user',
    };

    let nextList: TestPersona[];
    if (editingId) {
      nextList = personas.map((p) => (p.id === editingId ? item : p));
    } else {
      nextList = [...personas.filter((p) => p.id !== item.id), item];
    }

    await savePersonasToBackend(nextList);
  };

  const handleDeletePersona = async (idToDelete: string) => {
    const nextList = personas.filter((p) => p.id !== idToDelete);
    await savePersonasToBackend(nextList);
  };

  return (
    <div className="personas-tab" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionTitle
        actions={
          <button
            type="button"
            className="btn btn--small btn--primary"
            onClick={startNew}
            disabled={addingNew || saving}
          >
            + Add Test Persona
          </button>
        }
      >
        Test Personas & Credential Vault
      </SectionTitle>

      {/* Explanatory Banner */}
      <div
        className="panel"
        style={{
          background: 'var(--bg-inset)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 14,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>🛡️ Persistent Test Accounts & Anti-Pollution Vault</span>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Manage permanent test accounts here so AI agents and Playwright tests reuse fixed credentials
          instead of generating random dummy accounts on every run. When writing test steps, use dynamic tokens:
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 8,
            fontSize: 12,
            fontFamily: 'monospace',
          }}
        >
          <span style={{ background: 'rgba(59, 130, 246, 0.15)', padding: '2px 8px', borderRadius: 4, color: '#60a5fa' }}>
            &#123;&#123;user.email&#125;&#125;
          </span>
          <span style={{ background: 'rgba(59, 130, 246, 0.15)', padding: '2px 8px', borderRadius: 4, color: '#60a5fa' }}>
            &#123;&#123;user.password&#125;&#125;
          </span>
          <span style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: 4, color: '#34d399' }}>
            &#123;&#123;user.admin.email&#125;&#125;
          </span>
          <span style={{ background: 'rgba(245, 158, 11, 0.15)', padding: '2px 8px', borderRadius: 4, color: '#fbbf24' }}>
            &#123;&#123;timestamp&#125;&#125;
          </span>
          <span style={{ background: 'rgba(245, 158, 11, 0.15)', padding: '2px 8px', borderRadius: 4, color: '#fbbf24' }}>
            &#123;&#123;randomEmail&#125;&#125;
          </span>
        </div>
      </div>

      {/* Add / Edit Form Panel */}
      {addingNew || editingId ? (
        <div
          className="panel panel--form"
          style={{
            background: 'var(--bg-panel)',
            border: '1.5px solid var(--primary, #3b82f6)',
            borderRadius: 8,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14 }}>
            {editingId ? `Edit Persona: ${editingId}` : 'Add New Test Persona'}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <TextField
              label="Persona ID (slug)"
              value={formId}
              onChange={setFormId}
              placeholder="e.g. standard, admin, customer"
              disabled={Boolean(editingId)}
            />
            <TextField
              label="Display Label"
              value={formLabel}
              onChange={setFormLabel}
              placeholder="e.g. Standard Member"
            />
            <TextField
              label="Email Address"
              value={formEmail}
              onChange={setFormEmail}
              placeholder="user@example.com"
              type="email"
            />
            <TextField
              label="Password"
              value={formPassword}
              onChange={setFormPassword}
              placeholder="Secret password"
              type="text"
            />
            <TextField
              label="Role"
              value={formRole}
              onChange={setFormRole}
              placeholder="user, admin, guest"
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn--small btn--ghost" onClick={cancelForm}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={() => void handleSaveForm()}
              disabled={saving || !formId || !formEmail}
            >
              {saving ? 'Saving…' : 'Save Persona'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Persona Cards List */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {personas.map((persona) => {
          const isStandard = persona.id === 'standard' || persona.id === 'default';
          const isAdmin = persona.id === 'admin';

          return (
            <div
              key={persona.id}
              className="panel"
              style={{
                background: 'var(--bg-panel)',
                border: '1px solid var(--border)',
                borderTop: `3px solid ${isAdmin ? '#a855f7' : isStandard ? '#3b82f6' : '#10b981'}`,
                borderRadius: 8,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{isAdmin ? '👑' : isStandard ? '👤' : '🏷️'}</span>
                  <div>
                    <strong style={{ fontSize: 14 }}>{persona.label}</strong>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      id: <code>{persona.id}</code>
                    </div>
                  </div>
                </div>

                <span
                  style={{
                    fontSize: 10.5,
                    padding: '2px 6px',
                    borderRadius: 4,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {persona.role ?? 'user'}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                <div>
                  <span className="muted">Email: </span>
                  <Mono>{persona.email}</Mono>
                </div>
                <div>
                  <span className="muted">Password: </span>
                  <Mono>{persona.password ? '••••••••' : '(empty)'}</Mono>
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#38bdf8' }}>
                  Token:{' '}
                  <code>
                    &#123;&#123;
                    {isStandard ? 'user.email' : `user.${persona.id}.email`}
                    &#125;&#125;
                  </code>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="button"
                  className="btn btn--small btn--ghost"
                  onClick={() => startEdit(persona)}
                >
                  Edit
                </button>
                <ConfirmButton
                  label="Delete"
                  confirmLabel="Really delete persona?"
                  onConfirm={() => void handleDeletePersona(persona.id)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
